import React, { useEffect, useState } from 'react';
import { FileText, Clock, Target, Eye } from 'lucide-react';
import PageHero from '../components/PageHero';
import apiClient from '../utils/apiClient';
import { calculateStats } from '../utils/statsUtils';

const AGENT_LIST = [
  { name: 'Content Strategist', abbr: 'CS' },
  { name: 'Research Writer', abbr: 'RW' },
  { name: 'Managing Editor', abbr: 'ME' },
  { name: 'SEO Specialist & Copywriter', abbr: 'SE' },
  { name: 'Fact-Checker / Verification Specialist', abbr: 'FC' },
  { name: 'Philosopher / Thought Leader', abbr: 'TL' },
  { name: 'Subject Matter Expert (SME)', abbr: 'SM' },
];

const STEP_LEADS = {
  1: 'Content Strategist',
  2: 'Research Writer',
  3: 'Research Writer',
  4: 'Research Writer',
  5: 'Fact-Checker / Verification Specialist',
  6: 'Managing Editor',
  7: 'Content Strategist',
  8: 'SEO Specialist & Copywriter',
  9: 'Content Strategist',
};

export default function TheLens() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => setInstances(r.data?.results || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const stats = calculateStats(instances);
  const completed = instances.filter(i => i.status === 'completed');

  const styleEntries = Object.entries(stats.styleCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const styleTotal = styleEntries.reduce((s, [, n]) => s + n, 0);

  const formatEntries = Object.entries(stats.formatCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7);
  const maxFormatCount = formatEntries[0]?.[1] || 1;

  const agentStepCounts = {};
  AGENT_LIST.forEach(a => { agentStepCounts[a.name] = 0; });
  completed.forEach(inst => {
    Object.keys(inst.completed_steps || {}).forEach(stepNum => {
      const lead = STEP_LEADS[parseInt(stepNum)];
      if (lead && agentStepCounts[lead] !== undefined) {
        agentStepCounts[lead] += 1;
      }
    });
  });

  const page = {
    maxWidth: 900,
    margin: '0 auto',
    padding: isMobile ? '20px 16px 24px' : '24px',
    fontFamily: 'Inter, sans-serif',
    width: '100%',
    minWidth: 0,
  };
  const card = {
    background: '#1A2236',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: isMobile ? 16 : 20,
    minWidth: 0,
    overflow: 'hidden',
  };

  return (
    <div style={page}>
      <PageHero
        badge="ANALYTICS"
        title="The Lens"
        subtitle="Data-driven insights into your content engine. See how your AI team performs across every dimension."
      />

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <FileText size={20} color="#6B7280" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: isMobile ? 28 : 32, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', overflowWrap: 'anywhere' }}>
              {loading ? '—' : stats.completed}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 2 }}>Total Content Produced</div>
          </div>
        </div>

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Clock size={20} color="#6B7280" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: isMobile ? 28 : 32, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', overflowWrap: 'anywhere' }}>
              {loading ? '—' : stats.avgHours}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 2 }}>Avg. Production Time</div>
          </div>
        </div>

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Target size={20} color="#6B7280" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: isMobile ? 28 : 32, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9', overflowWrap: 'anywhere' }}>
              94/100*
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 2 }}>Content Quality Score</div>
            <div style={{ color: '#6B7280', fontSize: 11, marginTop: 4 }}>
              * Based on QA pass rate targets from system configuration
            </div>
          </div>
        </div>

        <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Eye size={20} color="#6B7280" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 32, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#F1F5F9' }}>—</div>
            <div style={{ color: '#9CA3AF', fontSize: 14, marginTop: 2 }}>Organic Reach</div>
            <div style={{ color: '#6B7280', fontSize: 11, marginTop: 4 }}>Connect analytics</div>
          </div>
        </div>
      </div>

      {/* Writing Style Distribution */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#F1F5F9', marginBottom: 20, fontFamily: 'Syne, sans-serif' }}>
          Writing Style Distribution
        </div>
        {loading ? (
          <div style={{ color: '#6B7280', fontSize: 13 }}>Loading...</div>
        ) : styleEntries.length === 0 ? (
          <div style={{ color: '#6B7280', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
            No data yet — complete workflows to see style distribution.
          </div>
        ) : styleEntries.map(([name, count]) => {
          const pct = styleTotal > 0 ? Math.round((count / styleTotal) * 100) : 0;
          return (
            <div key={name} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, color: '#CBD5E1', marginBottom: 6 }}>
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
                <span style={{ fontWeight: 600 }}>{pct}%</span>
              </div>
              <div style={{ height: 8, background: 'rgba(255,255,255,0.08)', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#1E3A8A', borderRadius: 999 }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Format Breakdown */}
      <div style={{ ...card, marginBottom: 32 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#F1F5F9', marginBottom: 20, fontFamily: 'Syne, sans-serif' }}>
          Content Format Breakdown
        </div>
        {loading ? (
          <div style={{ color: '#6B7280', fontSize: 13 }}>Loading...</div>
        ) : formatEntries.length === 0 ? (
          <div style={{ color: '#6B7280', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
            No data yet — complete workflows to see format breakdown.
          </div>
        ) : formatEntries.map(([name, count]) => {
          const pct = Math.round((count / maxFormatCount) * 100);
          const displayName = name.length > 18 ? name.substring(0, 16) + '...' : name;
          return (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, minWidth: 0 }}>
              <span style={{ fontSize: 13, color: '#CBD5E1', width: isMobile ? 96 : 130, flexShrink: 0, overflowWrap: 'anywhere' }}>{displayName}</span>
              <div style={{ flex: 1, height: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: '#F97316', borderRadius: 999 }} />
              </div>
              <span style={{ fontSize: 13, color: '#6B7280', width: 36, textAlign: 'right' }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Agent Performance */}
      <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, color: '#F1F5F9', marginBottom: 16 }}>
        Agent Performance
      </h2>
      {completed.length === 0 && !loading && (
        <div style={{ ...card, color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
          Performance metrics available after workflows complete.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        {AGENT_LIST.map(({ name, abbr }) => {
          const stepsCount = agentStepCounts[name] || 0;
          return (
            <div key={name} style={{ ...card }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: completed.length > 0 ? 12 : 0 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, background: '#1E3A8A',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 13, fontWeight: 700,
                }}>{abbr}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#F1F5F9', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>
                    {completed.length > 0 ? `${stepsCount} steps completed` : 'Ready'}
                  </div>
                </div>
              </div>
              {completed.length > 0 && stepsCount > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                    <span>Steps led</span>
                    <span style={{ fontWeight: 700, color: '#F97316' }}>{stepsCount}</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999 }}>
                    <div style={{
                      height: '100%',
                      width: `${Math.min((stepsCount / Math.max(...Object.values(agentStepCounts), 1)) * 100, 100)}%`,
                      background: '#1E3A8A',
                      borderRadius: 999,
                    }} />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
